import gsap from 'gsap';

const STYLE_ID = 'orvae-scene-transition-style';
const CSS = /* css */ `
.orvae-scene-transition {
  position: fixed;
  inset: 0;
  /* Below lil-gui's panel (z-index 1001) so the debug UI never washes out with the
     scene — only above the canvas (2026-07-05, user: the whole UI was fading too). */
  z-index: 500;
  pointer-events: none;
  opacity: 0;
  background-color: #fff;
}
.orvae-scene-transition__smoke {
  position: absolute;
  inset: -20%;
  width: 140%;
  height: 140%;
  background-repeat: repeat;
  background-size: 60% 60%;
  mix-blend-mode: multiply;
  animation: orvae-smoke-drift 14s linear infinite;
}
@keyframes orvae-smoke-drift {
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-4%, 3%) scale(1.08); }
  100% { transform: translate(0, 0) scale(1); }
}
`;

/** Draw one tileable grayscale "smoke" alpha mask (layered value noise) once, as a
 *  data URL — reused by every `SceneTransition` instance instead of redrawing per
 *  instance. Multiplied over the solid white base so DARKER noise patches let more of
 *  the outgoing scene read through mid-fade (wispy), while the container's own
 *  opacity still ramps to a fully solid white at the peak (2026-07-05 — the previous
 *  version used a few faint CSS radial-gradients, which read as a flat wash rather
 *  than actual smoke). */
function buildSmokeTexture(): string {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);

  const hash = (x: number, y: number, seed: number): number => {
    let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(seed | 0, 2246822519);
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
  };
  const valueNoise = (x: number, y: number, period: number, seed: number): number => {
    const xi = Math.floor(x) & (period - 1);
    const yi = Math.floor(y) & (period - 1);
    const xi1 = (xi + 1) & (period - 1);
    const yi1 = (yi + 1) & (period - 1);
    const xf = x - Math.floor(x);
    const yf = y - Math.floor(y);
    const u = xf * xf * (3 - 2 * xf);
    const v = yf * yf * (3 - 2 * yf);
    const a = hash(xi, yi, seed);
    const b = hash(xi1, yi, seed);
    const c = hash(xi, yi1, seed);
    const d = hash(xi1, yi1, seed);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  };
  const fbm = (x: number, y: number): number => {
    let value = 0;
    let amp = 0.55;
    let freq = 1;
    for (let o = 0; o < 4; o += 1) {
      value += amp * valueNoise(x * freq, y * freq, 8 * freq, 11 + o);
      freq *= 2;
      amp *= 0.55;
    }
    return value;
  };

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const u = (x / size) * 4; // tiles seamlessly (period-8 base noise, integer tiling)
      const v = (y / size) * 4;
      const n = fbm(u, v);
      // Bias toward mid-bright with strong contrast, so multiply-blend produces
      // distinct wispy patches instead of an even haze.
      const bright = Math.round(255 * Math.min(1, Math.max(0, n * 1.3 - 0.05)));
      const idx = (y * size + x) * 4;
      img.data[idx] = 255;
      img.data[idx + 1] = 255;
      img.data[idx + 2] = 255;
      img.data[idx + 3] = 255 - bright; // inverted into alpha: darker noise -> more visible white patch
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL('image/png');
}

/**
 * White "smoke" dissolve overlay — a plain DOM layer (NOT a postprocessing pass),
 * deliberately independent of the WebGL render pipeline and the `postprocessing`
 * enabled/disabled toggle, so it always works regardless of that setting (2026-07-05
 * — the postprocessing on/off toggle already caused enough grief; a transition this
 * central shouldn't depend on it). Used for BOTH the initial preload reveal and every
 * scroll-section boundary, so the visual language is identical everywhere (per user
 * request — "everything looks smooth with the transition, keep it consistent").
 *
 * Two layers: a solid white base (the container, whose opacity GSAP tweens 0→1→0) and
 * a drifting procedural-noise "smoke" texture multiply-blended on top, so mid-fade
 * reads as wispy smoke rather than a flat wash; `play()` calls `onPeak` while fully
 * opaque (mesh visibility swap + focal-length snap happen here, hidden from view).
 */
export class SceneTransition {
  private readonly el: HTMLDivElement;

  constructor(container: HTMLElement = document.body) {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      document.head.appendChild(style);
    }

    this.el = document.createElement('div');
    this.el.className = 'orvae-scene-transition';

    const smoke = document.createElement('div');
    smoke.className = 'orvae-scene-transition__smoke';
    smoke.style.backgroundImage = `url(${buildSmokeTexture()})`;
    this.el.appendChild(smoke);

    container.appendChild(this.el);
  }

  /** Fade to white, call `onPeak` while fully opaque, fade back out. Resolves once
   *  fully clear again. */
  play(onPeak: () => void, durationIn = 0.5, hold = 0.15, durationOut = 0.6): Promise<void> {
    return new Promise((resolve) => {
      gsap
        .timeline({ onComplete: resolve })
        .to(this.el, { opacity: 1, duration: durationIn, ease: 'power2.inOut' })
        .call(onPeak)
        .to({}, { duration: hold })
        .to(this.el, { opacity: 0, duration: durationOut, ease: 'power2.inOut' });
    });
  }

  dispose(): void {
    gsap.killTweensOf(this.el);
    this.el.remove();
  }
}
